import { useState, useEffect } from 'react'
import { FiSave, FiEye, FiEyeOff } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { useTranslation } from '../../hooks/useTranslation'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import toast from 'react-hot-toast'
import SideSlider from './SideSlider'

interface ChangePasswordSliderProps {
  isOpen: boolean
  onClose: () => void
}

export default function ChangePasswordSlider({ 
  isOpen, 
  onClose 
}: ChangePasswordSliderProps) {
  const { language } = useLanguageStore()
  const { translate } = useTranslation()
  const isRTL = language === 'ar'
  
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when slider opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setErrors({})
      setShowCurrentPassword(false)
      setShowNewPassword(false)
      setShowConfirmPassword(false)
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!currentPassword.trim()) {
      newErrors.currentPassword = isRTL ? 'كلمة المرور الحالية مطلوبة' : 'Current password is required'
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = isRTL ? 'كلمة المرور الجديدة مطلوبة' : 'New password is required'
    } else if (newPassword.length < 6) {
      newErrors.newPassword = isRTL ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters'
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = isRTL ? 'تأكيد كلمة المرور مطلوب' : 'Password confirmation is required'
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match'
    }

    if (currentPassword === newPassword) {
      newErrors.newPassword = isRTL ? 'كلمة المرور الجديدة يجب أن تكون مختلفة عن الحالية' : 'New password must be different from current password'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    try {
      await apiClient.post(API_ENDPOINTS.auth.changePassword, {
        currentPassword,
        newPassword,
      })

      toast.success(isRTL ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully')
      onClose()
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        (isRTL ? 'فشل تغيير كلمة المرور' : 'Failed to change password')
      toast.error(errorMessage)
      
      if (error?.response?.data?.message?.toLowerCase().includes('current password') || 
          error?.response?.data?.message?.toLowerCase().includes('كلمة المرور الحالية')) {
        setErrors({ currentPassword: errorMessage })
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <SideSlider
      isOpen={isOpen}
      onClose={onClose}
      isRTL={isRTL}
      title={isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
      widthClass="max-w-md"
      contentClassName="p-6"
    >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                {isRTL ? 'كلمة المرور الحالية' : 'Current Password'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value)
                    if (errors.currentPassword) {
                      setErrors({ ...errors, currentPassword: '' })
                    }
                  }}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder={isRTL ? 'أدخل كلمة المرور الحالية' : 'Enter current password'}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                {isRTL ? 'كلمة المرور الجديدة' : 'New Password'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    if (errors.newPassword) {
                      setErrors({ ...errors, newPassword: '' })
                    }
                    // Clear confirm password error if passwords match
                    if (errors.confirmPassword && e.target.value === confirmPassword) {
                      setErrors({ ...errors, confirmPassword: '' })
                    }
                  }}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.newPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder={isRTL ? 'أدخل كلمة المرور الجديدة' : 'Enter new password'}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {showNewPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>
              )}
              <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                {isRTL ? 'يجب أن تكون 6 أحرف على الأقل' : 'Must be at least 6 characters'}
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (errors.confirmPassword) {
                      setErrors({ ...errors, confirmPassword: '' })
                    }
                  }}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder={isRTL ? 'أعد إدخال كلمة المرور الجديدة' : 'Re-enter new password'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className={`flex items-center gap-3 pt-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--hover-bg)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-medium transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{isRTL ? 'جاري الحفظ...' : 'Saving...'}</span>
                  </>
                ) : (
                  <>
                    <FiSave className="text-lg" />
                    <span>{isRTL ? 'حفظ' : 'Save'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
    </SideSlider>
  )
}


